import {
    CheckRounded, CloseRounded, CreditCardOutlined, LocalOfferOutlined
} from '@mui/icons-material';
import React, { ReactNode } from 'react';
import { resolvePaymentMethods } from '../../resources/utils';
import Button from '../common/button';
import Checkbox from '../common/checkbox';

/**
 * Shared "Récapitulatif" order-summary card used by BOTH the cart (`cart/index`)
 * and the claim/history checkout (`claim/claim`). Everything structural lives here;
 * each page passes its own resolved strings + data. Page-specific bits are passed
 * through slots: `lines` (subtotal/discounts), `addCode` (cart only), `noticesAbove`
 * (claim alerts) and `belowCard` (loyalty banner / payment notices).
 */
export interface SummaryLine {
    key: string;
    label: ReactNode;
    value: ReactNode;
    discount?: boolean;
    /**
     * Only for discounts the customer applied themselves (code, voucher, loyalty
     * points): the line then carries a cross to take it back. Discounts computed
     * from the basket (family, internet, promo) leave this undefined.
     */
    onRemove?: () => void;
    removeLabel?: string;
}

interface Props {
    title: string;
    lines?: SummaryLine[];
    totalLabel: string;
    totalValue: string;
    vatLabel?: string;
    addCode?: { label: string; disabled?: boolean; onClick: () => void };
    noticesAbove?: ReactNode;
    checkBoxLabel: ReactNode;
    accept: boolean;
    onAcceptChange: () => void;
    onPay: (event: any) => void;
    payLoading?: boolean;
    payDisabled?: boolean;
    payLabel: string;
    onContinue: (event: any) => void;
    continueLabel: string;
    belowCard?: ReactNode;
    /** Raw `accepted_payment_methods` from the booked school (API-driven). */
    acceptedPaymentMethods?: string[];
}

function OrderSummary(props: Props) {
    const {
        title, lines, totalLabel, totalValue, vatLabel, addCode, noticesAbove,
        checkBoxLabel, accept, onAcceptChange, onPay, payLoading, payDisabled,
        payLabel, onContinue, continueLabel, belowCard, acceptedPaymentMethods,
    } = props;

    const paymentMethods = resolvePaymentMethods(acceptedPaymentMethods);

    return (
        <>
            <div className="__cart-summary-card">
                <div className="__cart-summary-head">
                    <h2>{title}</h2>
                </div>
                <div className="__cart-summary-body">
                    {(lines || []).map((line) => (
                        <div
                            className={`__cart-summary-line${line.discount ? ' __cart-discount-line' : ''}`}
                            key={line.key}
                        >
                            <span className="__cart-sl-k">
                                {line.discount ? <CheckRounded className="__cart-sl-check" /> : null}
                                <span
                                    className="__cart-sl-label"
                                    title={typeof line.label === 'string' ? line.label : undefined}
                                >
                                    {line.label}
                                </span>
                            </span>
                            <span className="__cart-sl-v">{line.value}</span>
                            {line.onRemove ? (
                                <button
                                    type="button"
                                    className="__cart-sl-remove"
                                    onClick={line.onRemove}
                                    title={line.removeLabel}
                                    aria-label={line.removeLabel}
                                >
                                    <CloseRounded />
                                </button>
                            ) : null}
                        </div>
                    ))}

                    {lines && lines.length > 0 ? <div className="__cart-summary-divider" /> : null}
                    <div className="__cart-summary-total">
                        <span className="__cart-st-k">{totalLabel}</span>
                        <span className="__cart-st-v">{totalValue}</span>
                    </div>
                    {vatLabel ? <div className="__cart-summary-vat">{vatLabel}</div> : null}

                    {addCode ? (
                        <button
                            type="button"
                            className="__cart-add-code"
                            disabled={addCode.disabled}
                            onClick={addCode.onClick}
                        >
                            <LocalOfferOutlined />
                            {addCode.label}
                        </button>
                    ) : null}
                </div>

                <div className="__cart-summary-foot">
                    {noticesAbove}
                    <div className="__cart-summary-checkbox">
                        <Checkbox
                            inputFirst
                            checked={accept}
                            onChange={onAcceptChange}
                            label={checkBoxLabel}
                            className=""
                            disabled={false}
                            inputClasses=""
                            labelClasses=""
                        />
                    </div>
                    <Button
                        buttonClasses="__cart-pay"
                        containerClasses="__cart-pay-wrap"
                        onClick={onPay}
                        loading={payLoading}
                        disabled={payDisabled}
                    >
                        <CreditCardOutlined />
                        {payLabel}
                    </Button>
                    <Button buttonClasses="__cart-ghost" containerClasses="__cart-ghost-wrap" onClick={onContinue}>
                        {continueLabel}
                    </Button>
                    {paymentMethods.length > 0 ? (
                        <div className="__cart-payment-chips">
                            {paymentMethods.map((pm) => (
                                <span className="__cart-pm" key={pm.code}>{pm.label}</span>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>
            {belowCard}
        </>
    );
}

export default OrderSummary;
